import {
  useState,
  useRef,
  useEffect,
  FC,
  Children,
  isValidElement,
  cloneElement,
  ReactNode,
} from 'react';

import { ICarouselInfiniteProps } from '../Carousel.types';
import { CarouselControls } from '../CarouselControls/CarouselControls';
import { useCarouselAnimation } from '../hooks/useCarouselAnimation';
import styles from './CarouselInfinite.styles.m.css';

function cloneChildrenWithKeys(childrenToClone: ReactNode[], prefix: string): ReactNode[] {
  return Children.map(childrenToClone, (child, index) => {
    if (isValidElement(child)) {
      return cloneElement(child, {
        key: `${prefix}-${child.key || index}`,
      });
    }
    return child;
  })!;
}

export const CarouselInfinite: FC<ICarouselInfiniteProps> = ({
  children,
  itemsToShow,
  itemWidth,
  gap,
  withShadow,
  activeIndex,
  onChangeActiveIndex,
}) => {
  const [currentIndex, setCurrentIndex] = useState(activeIndex || 0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const { animateTo, setPositionImmediate } = useCarouselAnimation({
    carouselRef,
  });

  const childrenArray = Children.toArray(children);
  const totalItems = childrenArray.length;
  const showNavigation = totalItems > itemsToShow;
  const totalWidth = totalItems * (itemWidth + gap);

  const clonedChildren = showNavigation
    ? [
        ...cloneChildrenWithKeys(childrenArray.slice(-itemsToShow), 'prefix'),
        ...childrenArray,
        ...cloneChildrenWithKeys(childrenArray.slice(0, itemsToShow), 'suffix'),
      ]
    : childrenArray;

  const getPosition = (index: number): number => {
    if (!showNavigation) {
      return 0;
    }

    return -((index + itemsToShow) * (itemWidth + gap));
  };

  const moveToIndex = (index: number): void => {
    const normalizedIndex = ((index % totalItems) + totalItems) % totalItems;
    setCurrentIndex(normalizedIndex);
    onChangeActiveIndex?.(normalizedIndex);

    const targetPosition = getPosition(index);

    // check whether we have reached the end or the beginning, and if so, adjust the position
    if (index !== normalizedIndex) {
      const adjustWidth = index > 0 ? totalWidth : -totalWidth;
      animateTo(targetPosition, adjustWidth);
    } else {
      animateTo(targetPosition);
    }
  };

  const handleNext = (): void => moveToIndex(currentIndex + 1);
  const handlePrev = (): void => moveToIndex(currentIndex - 1);

  useEffect(() => {
    const newPosition = getPosition(currentIndex);
    setPositionImmediate(newPosition);
  }, [itemWidth, gap, itemsToShow]);

  useEffect(() => {
    if (activeIndex !== undefined && activeIndex !== currentIndex) {
      moveToIndex(activeIndex);
    }
  }, [activeIndex]);

  return (
    <>
      <div className={styles.carouselWrapper}>
        <div
          ref={carouselRef}
          className={styles.carousel}
        >
          {Children.map(clonedChildren, (child) => (
            <div
              className={styles.carouselItem}
              style={{
                width: `${itemWidth}px`,
                marginRight: `${gap}px`,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      <CarouselControls
        showPrevButton={showNavigation}
        showNextButton={showNavigation}
        withShadow={withShadow}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </>
  );
};
